import React, { FC, PropsWithChildren, ReactElement } from "react"
import { DefaultProps, Group, GroupProps, MantineTheme, Text, UnstyledButton } from "@mantine/core"

export type TransparentButtonProps = DefaultProps & {
    variant?: "light" | "outline" | "transparent" | "icon"
    position?: GroupProps["position"]
    leftIcon?: ReactElement
    rightIcon?: ReactElement
    onClick?: () => void
}

const TransparentButton: FC<PropsWithChildren<TransparentButtonProps>> = ({
    children,
    variant,
    leftIcon: LIcon,
    rightIcon: RIcon,
    position = "center",
    ...props
}) => {
    return (
        <UnstyledButton
            sx={(theme: MantineTheme) => ({
                padding: "0.5rem",
                color: theme.colors.gray[0],
                borderRadius: "2rem",
                transition: "background-color 0.2s",
                opacity: 0.5,
                '&:hover, &[data-active="true"], a.active &:hover': {
                    opacity: 1,
                    backgroundColor: theme.fn.rgba(theme.colors.gray[0], 0.2),
                },
                "a.active &": {
                    opacity: 1,
                    backgroundColor: theme.fn.rgba(theme.colors.gray[0], 0.15),
                },
                ...(variant === "outline" && {
                    opacity: 1,
                    border: `1px solid ${theme.fn.rgba(theme.colors.gray[0], 0.5)}`,
                    '&:hover, &[data-active="true"], a.active &': {
                        backgroundColor: theme.fn.rgba(theme.colors.gray[0], 0.15),
                    },
                }),
                ...(variant === "light" && {
                    opacity: 1,
                    backgroundColor: theme.fn.rgba(theme.colors.gray[0], 0.15),
                    '&:hover, &[data-active="true"], a.active &': {
                        backgroundColor: theme.fn.rgba(theme.colors.gray[0], 0.2),
                    },
                }),
            })}
            {...props}
        >
            <Group
                spacing="sm"
                position={position}
                {...(variant === "icon" ? { w: "2rem", h: "2rem" } : { px: "0.75rem" })}
                noWrap
            >
                {LIcon}
                {typeof children === "string" ? (
                    <Text size="sm" weight={500} sx={theme => ({ fontFamily: theme.headings.fontFamily })}>
                        {children}
                    </Text>
                ) : (
                    children
                )}
                {RIcon}
            </Group>
        </UnstyledButton>
    )
}

export default TransparentButton
